<template>
  <div>
    <exo-table :root="_self">
      <exo-form :root="_self">
        <template v-slot:titleTooltip>
          <exo-help-dialog
            :color="'black--text'"
            :margin="'m-0'"
            title="Magnetic torquer"
          >
            The reaction magnetic torquer is an actuator use to control the attitude of the
            spacecraft.
            <br />
            <br />
            Here, the Lorentz's force is used to create a control torque using the Earth magnetic field.
            <br />
            <br />
            This type of actuator use only electrical power but magnetic torquer can't create a torque along magnetic field direction.
            Then orbit below 30° of inclination can be hard to control.
            <br />
            <br />
            The magnetic torque is computed with :
            <br />
            <katex-element expression="T = M \times B" />
            <br />
            <katex-element expression="T" /> correspond to the generated torque.
            <br />
            <katex-element expression="M" /> correspond to the magnetic torquer dipole moment.
            <br />
            <katex-element expression="B" /> correspond to the Earth magnetic field.
            <br />
            <br />
            The following parameter define a magnetic torquer :
            <ul>
              <li>The magnetic dipole moment.</li>
              <li>The magnetic torquer on-time (or duty cycle).</li>
              <li>The position of the magnetic torquer in spacecraft frame.</li>
              <li>The direction of the magnetic torquer in spacecraft frame.</li>
            </ul>
          </exo-help-dialog>
        </template>
        <exo-input-name alignWithParametricField :root="_self" />
        <exo-input-parametric :root="_self" name="magneticDipoleMoment" />
        <exo-input-parametric :root="_self" name="onTime" />
        <exo-card-form label="Position in spacecraft frame">
          <v-row no-gutters justify="start">
            <v-col cols="3" class="mr-15">
              <exo-input-parametric
                :root="_self"
                name="positionInSpacecraftFrameX"
              />
            </v-col>
            <v-col cols="3" class="mr-15">
              <exo-input-parametric
                :root="_self"
                name="positionInSpacecraftFrameY"
              />
            </v-col>
            <v-col cols="3" class="mr-15">
              <exo-input-parametric
                :root="_self"
                name="positionInSpacecraftFrameZ"
              />
            </v-col>
          </v-row>
        </exo-card-form>
        <exo-card-form label="Direction in spacecraft frame">
          <v-row no-gutters justify="start">
            <v-col cols="3" class="mr-15">
              <exo-input-parametric
                :root="_self"
                name="directionInSpacecraftFrameX"
              />
            </v-col>
            <v-col cols="3" class="mr-15">
              <exo-input-parametric
                :root="_self"
                name="directionInSpacecraftFrameY"
              />
            </v-col>
            <v-col cols="3" class="mr-15">
              <exo-input-parametric
                :root="_self"
                name="directionInSpacecraftFrameZ"
              />
            </v-col>
          </v-row>
        </exo-card-form>
        <exo-form-alert
          type="error"
          v-if="
            Number(inputs.directionInSpacecraftFrameX) === 0 &&
            Number(inputs.directionInSpacecraftFrameY) === 0 &&
            Number(inputs.directionInSpacecraftFrameZ) === 0
          "
        >
          The norm of the direction vector must be positive
        </exo-form-alert>
      </exo-form>
    </exo-table>
    <exo-modal-references :root="_self" :modalContent="modalRef" />
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import analyticalMixin from '../../mixins/analyticalFoms.js'

export default {
  name: 'analyticalmagnetictorquer',

  mixins: [formMixin, analyticalMixin],

  data() {
    return {
      tableFields: [
        { value: 'displayedName', text: 'Name', sortable: true },
        {
          value: 'positionInSpacecraftFrameX',
          text:
            'X position' +
            this.getDefaultUnitLabel('positionInSpacecraftFrameX'),
          sortable: true
        },
        {
          value: 'positionInSpacecraftFrameY',
          text:
            'Y position' +
            this.getDefaultUnitLabel('positionInSpacecraftFrameY'),
          sortable: true
        },
        {
          value: 'positionInSpacecraftFrameZ',
          text:
            'Z position' +
            this.getDefaultUnitLabel('positionInSpacecraftFrameZ'),
          sortable: true
        },
        {
          value: 'directionInSpacecraftFrameX',
          text:
            'X direction' +
            this.getDefaultUnitLabel('directionInSpacecraftFrameX'),
          sortable: true
        },
        {
          value: 'directionInSpacecraftFrameY',
          text:
            'Y direction' +
            this.getDefaultUnitLabel('directionInSpacecraftFrameY'),
          sortable: true
        },
        {
          value: 'directionInSpacecraftFrameZ',
          text:
            'Z direction' +
            this.getDefaultUnitLabel('directionInSpacecraftFrameZ'),
          sortable: true
        },
        {
          value: 'onTime',
          text: 'On-time' + this.getDefaultUnitLabel('onTime'),
          sortable: true
        },
        {
          value: 'magneticDipoleMoment',
          text:
            'Magnetic dipole moment' +
            this.getDefaultUnitLabel('magneticDipoleMoment'),
          sortable: true
        },
        { value: 'actions', text: 'Actions', sortable: false }
      ],
      modalRef: { content: {} },
      showModal: false,
      inputs: {
        positionInSpacecraftFrameX: this.getDefaultValue(
          'positionInSpacecraftFrameX'
        ),
        positionInSpacecraftFrameY: this.getDefaultValue(
          'positionInSpacecraftFrameY'
        ),
        positionInSpacecraftFrameZ: this.getDefaultValue(
          'positionInSpacecraftFrameZ'
        ),
        directionInSpacecraftFrameX: this.getDefaultValue(
          'directionInSpacecraftFrameX'
        ),
        directionInSpacecraftFrameY: this.getDefaultValue(
          'directionInSpacecraftFrameY'
        ),
        directionInSpacecraftFrameZ: this.getDefaultValue(
          'directionInSpacecraftFrameZ'
        ),
        onTime: this.getDefaultValue('onTime'),
        magneticDipoleMoment: this.getDefaultValue('magneticDipoleMoment'),
        parametricValues: {
          positionInSpacecraftFrameX: this.buildDefaultParametricValue(
            'positionInSpacecraftFrameX'
          ),
          positionInSpacecraftFrameY: this.buildDefaultParametricValue(
            'positionInSpacecraftFrameY'
          ),
          positionInSpacecraftFrameZ: this.buildDefaultParametricValue(
            'positionInSpacecraftFrameZ'
          ),
          directionInSpacecraftFrameX: this.buildDefaultParametricValue(
            'directionInSpacecraftFrameX'
          ),
          directionInSpacecraftFrameY: this.buildDefaultParametricValue(
            'directionInSpacecraftFrameY'
          ),
          directionInSpacecraftFrameZ: this.buildDefaultParametricValue(
            'directionInSpacecraftFrameZ'
          ),
          onTime: this.buildDefaultParametricValue('onTime'),
          magneticDipoleMoment: this.buildDefaultParametricValue(
            'magneticDipoleMoment'
          )
        }
      }
    }
  },

  methods: {
    getCurrentFormType() {
      return 'ParametricMagneticTorquerDefinition'
    },

    getFieldState(parentObject, fieldName, required) {
      let fieldState = analyticalMixin.methods.getFieldState.call(
        this,
        parentObject,
        fieldName,
        required
      )
      // Check if norm is correct
      if (JSON.stringify(fieldName).includes("directionInSpacecraftFrame") && this.inputs.directionInSpacecraftFrameX == 0 && this.inputs.directionInSpacecraftFrameY == 0 && this.inputs.directionInSpacecraftFrameZ == 0
      ) {
          fieldState.value = false
          fieldState.message =
            'Sorry, the norm of the direction must be above 0.'
        }
      return fieldState
    }
  }
}
</script>
